import {css, StyleSheet} from "aphrodite";
import {useMemo, type AriaAttributes} from "react";
import * as React from "react";

import SimpleMarkdown from "@khanacademy/simple-markdown";
import {addStyle, type StyleType} from "@khanacademy/wonder-blocks-core";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Markdown, type MarkdownRules} from "@khan/component-markdown";
import {
    convertToAbsoluteUrl,
    defaultRules,
    linkRule,
    markRule,
} from "@khan/component-markdown/rules";

import {GuideColors} from "./guide-colors.ts";
import {InternalPageFragmentLink} from "./messages/internal-page-fragment-link.tsx";
import {TypingIndicator} from "./typing-indicator.tsx";

interface Props extends AriaAttributes {
    text: string;
    showCursor?: boolean;
    style?: StyleType;
    onBeforeNav?: (url: string) => Promise<any>;
    onFragmentLinkClick?: (fragmentId: string) => void;
}

/**
 * escapeRegex taken from jquery.ui.autocomplete so we can use it without
 * importing the entire file.
 */
function escapeRegex(value: string): string {
    return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
}

/**
 * FormattedText renders a string with markdown formatting and supports
 * displaying a typing indicator.
 */
const FormattedText = (props: Props): React.ReactElement => {
    const {
        text,
        showCursor,
        style,
        onBeforeNav,
        onFragmentLinkClick,
        ...ariaAttributes
    } = props;
    const trimmedText = text.trim();
    const markdownRules = useMemo(
        () => _getMarkdownRules(onBeforeNav, onFragmentLinkClick),
        [onBeforeNav, onFragmentLinkClick],
    );

    return (
        <Markdown
            {...ariaAttributes}
            style={[styles.formattedText, style]}
            source={showCursor ? trimmedText + CURSOR_PLACEHOLDER : trimmedText}
            overrideRules={markdownRules}
        />
    );
};

// NOTE(tony): In order to display a typing cursor inline, we append a
// placeholder to the end of the content and add some parsing rules so
// that the markdown renderer knows how to render it. The placeholder needs
// to be uniquely delimited so that it does not conflict with existing
// rules and to avoid being included in other match rules.
const CURSOR_PLACEHOLDER = "<!☻ typing-cursor!>";
const CURSOR_PLACEHOLDER_PATTERN = escapeRegex(CURSOR_PLACEHOLDER);
const CURSOR_PLACEHOLDER_R = new RegExp(
    // We wrap the cursor pattern in parenthesis since matcher functions are
    // required to have at least 1 capture. We only match the last cursor
    // placeholder at the end of string to avoid multiple typing indicators.
    "^(" + CURSOR_PLACEHOLDER_PATTERN + ")$",
);

const StyledMark = addStyle("mark");

const _getMarkdownRules = (
    onBeforeNav: Props["onBeforeNav"],
    onFragmentLinkClick: Props["onFragmentLinkClick"],
): MarkdownRules => {
    // Override the default markdown rules to handle special cases for Khanmigo
    // interactions. We spread the default rules here since we want to iterate
    // through them all and override the parsers.
    const rules: MarkdownRules = {
        ...defaultRules,
        escape: {
            ...SimpleMarkdown.defaultRules.escape,
            // We need to override the escape matcher so that it excludes matches
            // with the cursor placeholder.
            match: SimpleMarkdown.inlineRegex(
                new RegExp(
                    `^\\\\(?:(?!${CURSOR_PLACEHOLDER_PATTERN})([^0-9A-Za-z\\s]))`,
                ),
            ),
        },
        link: {
            ...linkRule,
            react: (node, output, state) => {
                if (node?.target?.startsWith("#")) {
                    return (
                        <InternalPageFragmentLink
                            key={state.key}
                            fragmentId={node.target.slice(1)}
                            onClick={onFragmentLinkClick}
                            children={output(node.content)}
                        />
                    );
                }

                if (!linkRule.react) {
                    return null;
                }
                const link = linkRule.react(node, output, state);
                if (!link || !React.isValidElement(link)) {
                    return link;
                }
                // Since the original markdown rules do not allow for props
                // to be injected into the rendered elements, we will clone
                // the result and provide additional props.
                return React.cloneElement(
                    link,
                    {
                        // Since we're opening links in a new tab, we use
                        // `safeWithNav` instead of the `beforeNav` prop since
                        // doing so, will trigger built-in popup blockers in
                        // some browsers. Additionally, the current tab will still
                        // exist, we don't have to block navigation on the callback.

                        safeWithNav: () => onBeforeNav?.(node.target),
                    } as any,
                    // Children of the elements do not get cloned so we need
                    // to provide it ourselves.
                    link.props.children,
                );
            },
        },
        mark: {
            ...markRule,
            react: (node, output, state) => {
                return (
                    <StyledMark key={state.key} style={styles.mark}>
                        {output(node.content)}
                    </StyledMark>
                );
            },
        },
        // On the off-chance that AI Guide returns a plain URL in non-markdown
        // format, rather than displaying the raw URL, we want to create a
        // hyperlink with "Link" as the title. We override defaultRules.url to
        // make node.content null causing the link output function to fall back
        // to rendering "Link" as the text instead of the URL.
        url: {
            ...SimpleMarkdown.defaultRules.url,
            parse: function (capture, parse, state) {
                const url = convertToAbsoluteUrl(capture[1]);
                // If the URL is invalid, we don't want to render it as a link,
                // so instead we'll treat it as a text node and render the
                // contents.
                if (!url) {
                    return {
                        type: "text",
                        content: capture[1],
                    };
                }

                return {
                    type: "link", // specify to use the link output function
                    content: null,
                    target: url,
                    title: null,
                };
            },
        },
        blockQuote: {
            ...SimpleMarkdown.defaultRules.blockQuote,
            react: (node, output, state) => {
                return (
                    <blockquote
                        key={state.key}
                        className={css(styles.blockQuote)}
                    >
                        {output(node.content)}
                    </blockquote>
                );
            },
        },
    } as any;

    // HACK(tony): Due to how parsing works, it's possible for rules to
    // capture the cursor placeholder before the `cursor` rule matches
    // regardless of the order of the rule. For example, a fenced code block
    // like the following would be parsed by the `fence` rule first:
    // ```
    //     print("example"); <!☻ typing-cursor!>
    //
    // In rules where the parsing/output isn't nested, the cursor rule will
    // never have a chance to match the placeholder if it's part of the
    // captured content. Since it's not simple to override every match rule
    // to exclude matching the cursor placeholder, we just remove the
    // placeholder if it ever appears in string outputs.
    return Object.entries(rules).reduce(
        (rules, [key, rule]) => {
            if (!rule?.react) {
                return {...rules, [key]: rule};
            }

            const originalOutputFn = rule.react;
            return {
                ...rules,
                [key]: {
                    ...rule,
                    react: (node, output, state) => {
                        if (
                            !Array.isArray(node) &&
                            typeof node.content === "string"
                        ) {
                            node.content = node.content.replaceAll(
                                CURSOR_PLACEHOLDER,
                                "",
                            );
                        }
                        return originalOutputFn(node, output, state);
                    },
                },
            } as any;
        },
        {
            // We don't want the cursor rule itself to be overriden in the
            // reducer so we define it here instead of where we define the
            // other overrides.
            cursor: {
                // Specify that this should be matched before any other default
                // formatting rules.
                order: Number.MIN_SAFE_INTEGER,
                match: SimpleMarkdown.anyScopeRegex(CURSOR_PLACEHOLDER_R),
                parse: () => ({}), // ignore captures
                react: (_, __, state) => {
                    return <TypingIndicator key={state.key} inline={true} />;
                },
            },
        },
    );
};

const styles = StyleSheet.create({
    formattedText: {
        padding: 0,
        margin: 0,
        fontSize: "inherit",
    },
    blockQuote: {
        borderInlineStart: `4px solid ${semanticColor.core.border.neutral.subtle}`,
        paddingInlineStart: spacing.xSmall_8,
    },
    mark: {
        color: "inherit",
        backgroundColor: GuideColors.pink,
        borderBlockEnd: `2px solid ${GuideColors.darkPurple}`,
    },
});

export {FormattedText};
